import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { useState, useRef, useCallback, useEffect } from "react";

// Images
const INTRO_IMAGE = "/images/luxury-ensuite-gold/after-1.jpg";
const CTA_IMAGE = "/images/luxury-bathroom/after_1_full_view.webp";

const STEPS = [
  {
    phase: "plan",
    num: 1,
    title: "Your Vision",
    desc: "A quick call to discuss your ideas, style, needs and budget.",
    image: "https://images.pexels.com/photos/5292233/pexels-photo-5292233.jpeg?auto=compress&cs=tinysrgb&w=600",
  },
  {
    phase: "plan",
    num: 2,
    title: "First Home Visit",
    desc: "We visit your home, take measurements and offer expert suggestions.",
    image: "https://images.pexels.com/photos/5691675/pexels-photo-5691675.jpeg?auto=compress&cs=tinysrgb&w=600",
  },
  {
    phase: "plan",
    num: 3,
    title: "AI Concept Design",
    desc: "We create realistic AI concepts so you can see your new bathroom before we build it.",
    image: "https://images.pexels.com/photos/4977446/pexels-photo-4977446.jpeg?auto=compress&cs=tinysrgb&w=600",
  },
  {
    phase: "choose",
    num: 4,
    title: "Refine & Perfect",
    desc: "We fine-tune the design until every detail is exactly how you want it.",
    image: "https://images.pexels.com/photos/8533353/pexels-photo-8533353.jpeg?auto=compress&cs=tinysrgb&w=600",
  },
  {
    phase: "choose",
    num: 5,
    title: "Transparent Quote",
    desc: "You\u2019ll receive a flexible quote tailored to your choices and vision.",
    image: "https://images.pexels.com/photos/8815849/pexels-photo-8815849.jpeg?auto=compress&cs=tinysrgb&w=600",
  },
  {
    phase: "choose",
    num: 6,
    title: "Materials & Showroom",
    desc: "We bring samples to you and help source the perfect materials, with showroom guidance if needed.",
    image: "https://images.pexels.com/photos/4977410/pexels-photo-4977410.jpeg?auto=compress&cs=tinysrgb&w=600",
  },
  {
    phase: "build",
    num: 7,
    title: "Strip-Out & Preparation",
    desc: "We protect your home and remove everything, ready for transformation.",
    image: "https://images.pexels.com/photos/15798781/pexels-photo-15798781.jpeg?auto=compress&cs=tinysrgb&w=600",
  },
  {
    phase: "build",
    num: 8,
    title: "Plumbing & Preparation",
    desc: "All pipework, electrics, waterproofing and surfaces are prepared to the highest standard.",
    image: "https://customer-assets-wrfwihn1.emergentagent.net/job_f4afc9c8-562b-4ae6-a3a1-517d9e3e9297/artifacts/vkmkpu1w_copper%20pipes.jpg",
  },
  {
    phase: "build",
    num: 9,
    title: "Installation & Finishing",
    desc: "Tiling, fittings and finishing touches completed with precision and attention to every detail.",
    image: "https://static.prod-images.emergentagent.com/jobs/f4afc9c8-562b-4ae6-a3a1-517d9e3e9297/images/fe22a861e4f083eb8b191d7b89b14514df2d7d595e35f3100ffb009e417b517c.jpeg",
  },
  {
    phase: "build",
    num: 10,
    title: "Final Walkthrough",
    desc: "We inspect everything together and hand over your beautiful new bathroom.",
    image: "/images/luxury-ensuite-gold/after-7.jpg",
  },
];

const PHASES = [
  { id: "plan", label: "Plan", icon: PlanIcon, bg: "bg-[#F8F5F0]" },
  { id: "choose", label: "Choose", bg: "bg-[#F3EFE9]", icon: ChooseIcon },
  { id: "build", label: "Build", bg: "bg-[#EDE8E1]", icon: BuildIcon },
];

function PlanIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-bronze">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  );
}

function ChooseIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-bronze">
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
    </svg>
  );
}

function BuildIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-bronze">
      <path d="M2 20h20" />
      <path d="M5 20V8l7-5 7 5v12" />
      <path d="M9 20v-4h6v4" />
      <rect x="9" y="10" width="6" height="4" />
    </svg>
  );
}

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

// Step Navigation Bar
const StepNavBar = ({ activeStep, onStepClick }) => (
  <div className="flex justify-center" data-testid="process-step-nav">
    <div className="inline-flex items-center bg-white rounded-full px-4 py-2.5 shadow-sm border border-charcoal/5">
      {Array.from({ length: 10 }, (_, i) => i + 1).map((step, idx) => (
        <div key={step} className="flex items-center">
          <button
            onClick={() => onStepClick(step)}
            data-testid={`process-step-${step}`}
            className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-all duration-300 ${
              activeStep === step
                ? "bg-bronze text-white shadow-md scale-110"
                : "bg-transparent text-charcoal/40 hover:text-charcoal/70 hover:bg-charcoal/5"
            }`}
          >
            {step}
          </button>
          {idx < 9 && (
            <div className={`w-4 sm:w-6 h-[2px] mx-0.5 transition-colors duration-300 ${
              step < activeStep ? "bg-bronze/40" : "bg-charcoal/10"
            }`} />
          )}
        </div>
      ))}
    </div>
  </div>
);

// Phase Card Row with arrows
const PhaseRow = ({ steps, onStepClick }) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 5);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 5);
  }, []);

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (el) el.addEventListener("scroll", checkScroll, { passive: true });
    window.addEventListener("resize", checkScroll);
    return () => {
      if (el) el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, [checkScroll]);

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 300, behavior: "smooth" });
  };

  return (
    <div className="relative group/row">
      {/* Left Arrow */}
      <button
        onClick={() => scroll(-1)}
        className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 z-10 w-10 h-10 rounded-full bg-white shadow-lg border border-charcoal/10 flex items-center justify-center text-charcoal/50 hover:text-bronze hover:border-bronze/30 transition-all duration-300 ${
          canScrollLeft ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        data-testid="phase-scroll-left"
      >
        <ChevronLeft size={18} />
      </button>

      {/* Cards */}
      <div
        ref={scrollRef}
        className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory"
      >
        {steps.map((step) => (
          <motion.div
            key={step.num}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-30px" }}
            variants={fadeUp}
            transition={{ duration: 0.5, delay: (step.num % 3) * 0.1 }}
            className="flex-shrink-0 snap-start w-[260px] sm:w-[280px] md:w-auto md:flex-1 cursor-pointer"
            onClick={() => onStepClick(step.num)}
            data-testid={`process-card-step-${step.num}`}
          >
            {/* Image */}
            <div className="relative overflow-hidden rounded-lg aspect-[4/3] mb-4 group">
              <img
                src={step.image}
                alt={step.title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
              />
            </div>

            {/* Number + Title */}
            <div className="flex items-center gap-3 mb-2">
              <span className="w-7 h-7 rounded-full bg-bronze text-white flex items-center justify-center text-[11px] font-bold flex-shrink-0">
                {step.num}
              </span>
              <h4 className="font-heading text-base text-charcoal">{step.title}</h4>
            </div>

            {/* Description */}
            <p className="font-body text-sm text-charcoal/50 leading-relaxed pl-10">
              {step.desc}
            </p>
          </motion.div>
        ))}
      </div>

      {/* Right Arrow */}
      <button
        onClick={() => scroll(1)}
        className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 z-10 w-10 h-10 rounded-full bg-white shadow-lg border border-charcoal/10 flex items-center justify-center text-charcoal/50 hover:text-bronze hover:border-bronze/30 transition-all duration-300 ${
          canScrollRight ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        data-testid="phase-scroll-right"
      >
        <ChevronRight size={18} />
      </button>
    </div>
  );
};

// Trust Icon
function TrustIcon({ type }) {
  if (type === "craft")
    return (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" className="text-bronze">
        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
      </svg>
    );
  if (type === "pricing")
    return (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" className="text-bronze">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    );
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" className="text-bronze">
      <path d="M6 9H4.5a2.5 2.5 0 010-5C7 4 9 7 12 7s5-3 7.5-3a2.5 2.5 0 010 5H18" />
      <path d="M18 9v7a5 5 0 01-5 5h-2a5 5 0 01-5-5V9" />
      <path d="M12 7v14" />
    </svg>
  );
}

// Main Page Component
export default function OurProcessPage() {
  const [activeStep, setActiveStep] = useState(1);
  const phaseRefs = useRef({});

  const scrollToStep = useCallback((step) => {
    setActiveStep(step);
    const s = STEPS.find((st) => st.num === step);
    if (!s) return;
    const el = phaseRefs.current[s.phase];
    if (el) {
      const y = el.getBoundingClientRect().top + window.pageYOffset - 100;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  }, []);

  const registerRef = useCallback((phase, el) => {
    if (el) phaseRefs.current[phase] = el;
  }, []);

  return (
    <div data-testid="our-process-page" className="bg-[#FAF8F5]">

      {/* ===== INTRO ===== */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-12 pb-10">
        <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
          {/* Left */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            transition={{ duration: 0.8 }}
          >
            <span className="font-body text-xs uppercase tracking-widest text-bronze mb-4 block">
              Our Process
            </span>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-charcoal mb-5 leading-tight">
              From Your First Idea<br />to the Final Detail
            </h1>
            <p className="font-body text-sm text-charcoal/55 leading-relaxed max-w-md mb-8">
              A clear, step-by-step process designed to make creating your new bathroom simple, considered and stress-free.
            </p>
            <Link
              to="/contact"
              data-testid="process-hero-cta"
              className="group relative inline-flex items-center justify-center gap-2 uppercase tracking-widest text-xs font-semibold px-8 py-4 bg-bronze text-charcoal rounded-sm overflow-hidden transition-all duration-500"
            >
              <span className="relative z-10 flex items-center gap-2">
                Start Your Project
                <ArrowRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
              </span>
              <div className="absolute inset-0 bg-bronze-light transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
            </Link>
          </motion.div>

          {/* Right - Image */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative rounded-xl overflow-hidden aspect-[4/3] shadow-lg"
          >
            <img
              src={INTRO_IMAGE}
              alt="Luxury completed bathroom"
              className="w-full h-full object-cover"
              data-testid="process-intro-image"
            />
          </motion.div>
        </div>
      </section>

      {/* ===== STEP NAV BAR ===== */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-12">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.6, delay: 0.35 }}
        >
          <StepNavBar activeStep={activeStep} onStepClick={scrollToStep} />
        </motion.div>
      </section>

      {/* ===== PHASES ===== */}
      {PHASES.map((phase) => {
        const phaseSteps = STEPS.filter((s) => s.phase === phase.id);
        const Icon = phase.icon;
        return (
          <section
            key={phase.id}
            ref={(el) => registerRef(phase.id, el)}
            className={`${phase.bg} py-14 md:py-16`}
            data-testid={`process-phase-${phase.id}`}
          >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
              {/* Phase Header */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                transition={{ duration: 0.6 }}
                className="flex items-center gap-3 mb-10"
              >
                <div className="w-10 h-10 rounded-full bg-bronze/10 flex items-center justify-center">
                  <Icon />
                </div>
                <h2 className="font-body text-xs uppercase tracking-[0.2em] text-bronze font-bold">
                  {phase.label}
                </h2>
              </motion.div>

              {/* Cards */}
              <PhaseRow steps={phaseSteps} onStepClick={setActiveStep} />
            </div>
          </section>
        );
      })}

      {/* ===== BOTTOM CTA ===== */}
      <section className="bg-[#F8F5F0] border-t border-charcoal/5" data-testid="process-bottom-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
          <div className="grid md:grid-cols-[auto_1fr_auto] gap-8 items-center">
            {/* Left Image */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              transition={{ duration: 0.7 }}
              className="hidden md:block w-48 lg:w-56 rounded-xl overflow-hidden aspect-[3/4] shadow-md"
            >
              <img
                src={CTA_IMAGE}
                alt="Luxury bathroom"
                className="w-full h-full object-cover"
              />
            </motion.div>

            {/* Center Text */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              transition={{ duration: 0.7, delay: 0.1 }}
            >
              <h3 className="font-heading text-2xl sm:text-3xl text-charcoal mb-2">
                Have an idea in mind?
              </h3>
              <p className="font-body text-sm text-charcoal/50">
                Let&apos;s start a conversation about your project.
              </p>
            </motion.div>

            {/* CTA Button */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              <Link
                to="/contact"
                data-testid="process-cta-contact"
                className="group relative inline-flex items-center justify-center gap-2 uppercase tracking-widest text-xs font-semibold px-8 py-4 bg-bronze text-charcoal rounded-sm overflow-hidden transition-all duration-500 whitespace-nowrap"
              >
                <span className="relative z-10 flex items-center gap-2">
                  Discuss Your Project
                  <ArrowRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
                <div className="absolute inset-0 bg-bronze-light transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
              </Link>
            </motion.div>
          </div>

          {/* Trust Points */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-8 sm:gap-14 mt-12 pt-10 border-t border-charcoal/5"
          >
            {[
              { icon: "craft", label: "Expert\nCraftsmanship" },
              { icon: "pricing", label: "Honest,\nTransparent Pricing" },
              { icon: "quality", label: "Quality Finishes\nBuilt to Last" },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-bronze/10 flex items-center justify-center flex-shrink-0">
                  <TrustIcon type={item.icon} />
                </div>
                <span className="font-body text-[11px] uppercase tracking-wider text-charcoal/60 whitespace-pre-line leading-tight font-medium">
                  {item.label}
                </span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
}
